import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import {
  type PaginationQueryParams,
  PaginationSchema,
} from '../domain/pagination';
import { type PaginatedBundle, PaginatedBundleSchema } from '../domain/schema';
import type { WithId } from '../types';

import { ContentTypeSchema, MediaDataSchema } from './base';

export const CreateUploadURLSchema = z.object({
  title: z.string().min(1),
  contentType: ContentTypeSchema,
  patientId: UUIDSchema,
});

export const MediaSchema = MediaDataSchema.extend({
  patient: DisplayReferenceSchema,
  createdBy: DisplayReferenceSchema,
  createdDateTime: z.string(),
  admission: DisplayReferenceSchema.optional(),
  partOf: z.array(DisplayReferenceSchema).optional(),
});

export const MediaItemCreateSchema = z.array(
  MediaSchema.extend({
    data: z.string(),
  }),
);

export const PatientMediaCreateSchema = z.object({
  patient: UUIDSchema,
  media: z.array(MediaDataSchema),
  admission: DisplayReferenceSchema.optional(),
});

export const MediaCreateSchema = z.object({
  media: z.array(MediaDataSchema),
});

export const PatientMediaSearchSchema = PaginationSchema.extend({
  subject: UUIDSchema,
});

export const MediaGetResponseSchema = MediaSchema.partial().extend({
  id: UUIDSchema,
});

/**
 * Data is meant to be used when uploading a file to S3. The request should be sent as a
 * multipart form with the `file` added to the body
 */
export const CreateUploadUrlResponseSchema = z.object({
  url: z.string(),
  key: z.string(),
  /**
   * The data here should be included in the S3 request in order to allow the request to be
   * authenticated
   */
  formData: z.record(z.string(), z.string()),
});

export type CreateMediaData = z.infer<typeof MediaDataSchema>;

export type CreateMediaRequest = z.infer<typeof MediaCreateSchema>;

export type PatientMediaCreateRequest = z.infer<
  typeof PatientMediaCreateSchema
>;

export type MediaCreateItem = z.infer<typeof MediaItemCreateSchema>;

export type CreateMediaResponse = {
  media: MediaGetResponse[];
};

export const MediaGetAllResponseSchema = PaginatedBundleSchema.extend({
  media: MediaGetResponseSchema.array(),
});

export interface GetAllMediaResponse extends PaginatedBundle {
  media: MediaGetResponse[];
}

export type Media = z.infer<typeof MediaSchema>;
export type MediaGetResponse = WithId<Partial<Media>>;

export type GetAllMediaParams = PaginationQueryParams;
export type GetAllMediaSearchParams = z.infer<typeof PatientMediaSearchSchema>;

export type CreateUploadUrlRequest = z.infer<typeof CreateUploadURLSchema>;

export type CreateUploadUrlResponse = z.infer<
  typeof CreateUploadUrlResponseSchema
>;

export interface CreateDownloadUrlResponse {
  url: string;
}
